import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { useLocation } from './LocationContext';

type BusinessType = 'fish' | 'goat';

interface BusinessTypeContextType {
  businessType: BusinessType;
  setBusinessType: (type: BusinessType) => void;
  availableBusinessTypes: BusinessType[];
  hasMultipleTypes: boolean;
}

const BusinessTypeContext = createContext<BusinessTypeContextType | undefined>(undefined);

export const BusinessTypeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { selectedLocation, isInitialized: locationInitialized } = useLocation();
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  
  // Get storage key for this location
  const getStorageKey = (locationId: string | null) => {
    return locationId ? `business_type_${locationId}` : 'business_type_default';
  };
  
  // Initialize state - default to 'fish', will be updated in useEffect when location loads
  const [businessType, setBusinessTypeState] = useState<BusinessType>('fish');
  const [isInitialized, setIsInitialized] = useState(false);

  // Get available business types from selected location
  const availableBusinessTypes: BusinessType[] = (selectedLocation?.business_types || ['fish']) as BusinessType[];
  const hasMultipleTypes = availableBusinessTypes.length > 1;

  // Helper function to check if current route is valid for business type
  const isRouteValidForBusinessType = (pathname: string, type: BusinessType): boolean => {
    // Fish-specific routes (check these first before shared routes)
    const fishRoutes = [
      '/feed-logs',
      '/medicine-logs',
      '/water-quality-logs',
      '/fish-seed-logs',
      '/fish-sampling-logs',
      '/dead-fish-logs',
      '/cycle-planning',
      '/retail-sales',
      '/reports/compliance',
      '/reports/feed-trends',
      '/reports/medicine-trends',
      '/reports/mortality',
      '/reports/inventory-snapshot',
      '/reports/cycle-count',
      '/reports/revenue-projections',
      '/reports/cost-analysis',
    ];

    // Goat-specific routes (check these first before shared routes)
    const goatRoutes = [
      '/goat/',
      '/goat/dashboard',
      '/goat/data-entry',
    ];

    // Shared routes that work for both business types
    const sharedRoutes = [
      '/inventory',
      '/reports', // Main reports page is shared, but sub-routes are checked above
      '/admin',
      '/locations',
      '/profile',
      '/payment-requests', // Payment requests work for both fish and goat
      '/payroll', // Payroll works for both fish and goat
      '/assets', // Assets work for both fish and goat
      '/tasks', // Tasks work for both fish and goat
    ];

    // Check specific routes first (they take precedence over shared routes)
    if (type === 'fish') {
      // Check if it's a fish-specific route
      if (fishRoutes.some(route => pathname.startsWith(route))) {
        return true;
      }
      // Check if it's a goat-specific route (should return false for fish)
      if (goatRoutes.some(route => pathname.startsWith(route))) {
        return false;
      }
      // Check if it's the fish dashboard
      if (pathname === '/dashboard' || pathname.startsWith('/dashboard')) {
        return true;
      }
      // Check if it's the fish data-entry
      if (pathname.startsWith('/data-entry')) {
        return true;
      }
      // Check if it's a shared route
      return sharedRoutes.some(route => pathname.startsWith(route));
    } else if (type === 'goat') {
      // Check if it's a goat-specific route
      if (goatRoutes.some(route => pathname.startsWith(route))) {
        return true;
      }
      // Check if it's a fish-specific route (should return false for goat)
      if (fishRoutes.some(route => pathname.startsWith(route))) {
        return false;
      }
      // For goat, /dashboard is not valid (only /goat/dashboard is)
      if (pathname === '/dashboard' || pathname.startsWith('/dashboard')) {
        return false;
      }
      // For goat, /data-entry is not valid (only /goat/data-entry is)
      if (pathname.startsWith('/data-entry')) {
        return false;
      }
      // Check if it's a shared route
      return sharedRoutes.some(route => pathname.startsWith(route));
    }

    return false;
  };

  // Auto-select business type when location changes
  useEffect(() => {
    if (selectedLocation) {
      const types = selectedLocation.business_types || ['fish'];
      
      // If we're on a goat route and location supports goat, prioritize goat
      if (routerLocation.pathname.startsWith('/goat/') && types.includes('goat')) {
        setBusinessTypeState('goat');
        localStorage.setItem(getStorageKey(selectedLocation.id), 'goat');
        setIsInitialized(true);
        return;
      }
      
      // Try to load from localStorage first
      const stored = localStorage.getItem(getStorageKey(selectedLocation.id));
      if (stored && (stored === 'fish' || stored === 'goat') && types.includes(stored)) {
        setBusinessTypeState(stored as BusinessType);
        setIsInitialized(true);
        return;
      }
      
      // If current selection is not available in new location, reset
      setBusinessTypeState((currentType) => {
        if (!types.includes(currentType)) {
          // Default to 'fish' if available, otherwise first type
          const defaultType = (types.includes('fish') ? 'fish' : types[0]) as BusinessType;
          localStorage.setItem(getStorageKey(selectedLocation.id), defaultType);
          setIsInitialized(true);
          return defaultType;
        }
        // Current selection is valid, make sure it's saved
        localStorage.setItem(getStorageKey(selectedLocation.id), currentType);
        setIsInitialized(true);
        return currentType;
      });
    } else {
      // No location selected, but if we're on a goat route, try to infer goat
      if (routerLocation.pathname.startsWith('/goat/')) {
        setBusinessTypeState('goat');
        setIsInitialized(true);
      } else {
        setBusinessTypeState('fish');
        setIsInitialized(true);
      }
    }
  }, [selectedLocation?.id, routerLocation.pathname]); // Depend on location ID and pathname

  const setBusinessType = (type: BusinessType) => {
    // Only allow setting if the type is available for current location
    if (availableBusinessTypes.includes(type)) {
      const previousType = businessType;
      setBusinessTypeState(type);
      // Persist to localStorage
      if (selectedLocation) {
        localStorage.setItem(getStorageKey(selectedLocation.id), type);
      }
      
      // If business type changed and current route is not valid for new type, redirect appropriately
      if (previousType !== type && !isRouteValidForBusinessType(routerLocation.pathname, type)) {
        // If switching to goat and currently on data-entry, redirect to goat data-entry
        if (type === 'goat' && routerLocation.pathname.startsWith('/data-entry')) {
          navigate('/goat/data-entry/', { replace: true });
        } else {
          // Otherwise redirect to appropriate dashboard
          const dashboardPath = type === 'goat' ? '/goat/dashboard' : '/dashboard';
          navigate(dashboardPath, { replace: true });
        }
      }
    }
  };

  // Watch for business type changes and redirect if needed (handles programmatic changes)
  useEffect(() => {
    // Don't redirect on auth, location selection, or payment request pages
    if (routerLocation.pathname === '/auth' || 
        routerLocation.pathname === '/locations' ||
        routerLocation.pathname.startsWith('/payment-requests')) {
      return;
    }
    
    // Don't redirect until both location and business type are initialized (prevents redirect on page refresh)
    if (!locationInitialized || !isInitialized) {
      return;
    }
    
    // Never redirect away from goat routes - let the route handle its own validation
    if (routerLocation.pathname.startsWith('/goat/')) {
      return;
    }
    
    // Only redirect if location is selected and route is invalid for current business type
    if (selectedLocation && !isRouteValidForBusinessType(routerLocation.pathname, businessType)) {
      // If on data-entry and switching to goat, redirect to goat data-entry
      if (businessType === 'goat' && routerLocation.pathname.startsWith('/data-entry')) {
        navigate('/goat/data-entry/', { replace: true });
      } else {
        // Otherwise redirect to appropriate dashboard
        const dashboardPath = businessType === 'goat' ? '/goat/dashboard' : '/dashboard';
        navigate(dashboardPath, { replace: true });
      }
    }
  }, [businessType, selectedLocation?.id, routerLocation.pathname, isInitialized, locationInitialized]); // Check when business type, location, pathname, or initialization changes

  return (
    <BusinessTypeContext.Provider
      value={{
        businessType,
        setBusinessType,
        availableBusinessTypes,
        hasMultipleTypes,
      }}
    >
      {children}
    </BusinessTypeContext.Provider>
  );
};

export const useBusinessTypeContext = (): BusinessTypeContextType => {
  const context = useContext(BusinessTypeContext);
  if (context === undefined) {
    throw new Error('useBusinessTypeContext must be used within a BusinessTypeProvider');
  }
  return context;
};
